import React, { useMemo } from "react";
import {
    Dialog,
    DialogContent,
    Typography,
    Chip,
    Box,
    IconButton,
    LinearProgress,
    Paper,
} from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import { MdLineStyle } from "react-icons/md";
import { IoReorderThreeOutline } from "react-icons/io5";
import { LuClockArrowDown } from "react-icons/lu";
import { CiCalendar } from "react-icons/ci";
import { useParams } from "react-router-dom";

import { useGetUpcomingCriticalActivitiesQuery } from "store/slices/apis/scheduleSlice";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// 🔁 keep header + rows in perfect sync
const GRID_COLS = "140px 1fr 220px 120px";

// 🗓️ small util to show dd/MM/yyyy
const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date)) return "";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
};

// ---- helper: build hierarchy from flat API data ----
const buildActivityTree = (rows) => {
    const nodesById = new Map();

    rows.forEach((row) => {
        if (!row) return;

        const activityId =
            (row.ActivityId ?? row.activityId ?? "").toString().trim();
        if (!activityId) return;

        const parentTaskIdRaw =
            row.ParentTaskId ?? row.parentTaskId ?? null;
        const parentId =
            parentTaskIdRaw !== null && parentTaskIdRaw !== undefined
                ? parentTaskIdRaw.toString()
                : null;

        const activityCode =
            row.ActivityCode ?? row.activityCode ?? "";

        const baselineEndDate =
            row.forecastEndDate ? row?.forecastEndDate : row.baselineEndDate ?? "";
        const percentageCompleteRaw =
            row.PercentageComplete ?? row.percentageComplete ?? 0;

        // ⬇️ if backend sends Critical / critical we capture it; otherwise false
        const isCritical =
            row.Critical ?? row.critical ?? false;

        const existing = nodesById.get(activityId);

        const node = existing || {
            id: activityId,                      // internal key for expand/collapse
            parentId,                            // internal parent
            code: activityCode || activityId,    // what we display as "Activity Code"
            name: row.ActivityName ?? row.activityName ?? "",
            baselineEndDate,
            percentageComplete: Number(
                percentageCompleteRaw !== undefined && percentageCompleteRaw !== null
                    ? percentageCompleteRaw
                    : 0
            ),
            isCritical: Boolean(isCritical),
            children: [],
        };

        node.parentId = parentId;
        node.name = node.name || row.ActivityName || row.activityName || "";
        node.baselineEndDate = baselineEndDate || node.baselineEndDate;
        if (
            node.percentageComplete === 0 &&
            percentageCompleteRaw !== undefined &&
            percentageCompleteRaw !== null
        ) {
            node.percentageComplete = Number(percentageCompleteRaw);
        }
        node.isCritical = Boolean(isCritical);

        nodesById.set(activityId, node);
    });

    const roots = [];

    nodesById.forEach((node) => {
        if (!node.parentId) {
            roots.push(node);
        } else {
            const parent = nodesById.get(node.parentId.toString());
            if (parent) {
                parent.children.push(node);
            } else {
                roots.push(node);
            }
        }
    });

    const sortNodes = (list) => {
        list.sort((a, b) => {
            const aNum = Number(a.id);
            const bNum = Number(b.id);
            if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
                return aNum - bNum;
            }
            return a.id.localeCompare(b.id, undefined, { numeric: true });
        });
        list.forEach((n) => sortNodes(n.children));
    };

    sortNodes(roots);
    return roots;
};

const UpcomingCriticalActivityDialog = ({ open, onClose, startDate, endDate, activityCount, category }) => {
    const { projectId } = useParams();

    const { data, isLoading, isError } = useGetUpcomingCriticalActivitiesQuery({ projectId, category, startDate, endDate });

    const flatRows = useMemo(() => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (Array.isArray(data.data)) return data.data;
        return [];
    }, [data]);

    const treeData = useMemo(() => buildActivityTree(flatRows), [flatRows]);

    const [expandedIds, setExpandedIds] = React.useState(new Set());

    const toggleNode = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const renderNode = (node, level = 0) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedIds.has(node.id);

        return (
            <React.Fragment key={node.id}>
                <Box
                    sx={{
                        position: "relative",
                        display: "grid",
                        gridTemplateColumns: GRID_COLS,
                        gap: 1,
                        alignItems: "center",
                        py: 0.5,
                        pl: `${level * 16}px`,
                        borderBottom: "1px solid #F1F1F1",
                        backgroundColor: node.isCritical ? "#FFF7F7" : "transparent",

                        // 🔹 dotted vertical guide for hierarchy
                        ...(level > 0 && {
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                left: `${(level * 16) - 8}px`,
                                top: 0,
                                bottom: 0,
                                width: "1px",
                                borderLeft: "1px dashed #D0D0D0",
                            },
                        }),
                    }}
                >
                    {/* Activity Code + expand/collapse toggle */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {hasChildren ? (
                            <IconButton
                                size="small"
                                onClick={() => toggleNode(node.id)}
                                sx={{
                                    color: "#4B5563",
                                    "&:hover": { background: "transparent" },
                                    mr: 1,
                                }}
                            >
                                {isExpanded ? (
                                    <ExpandMoreIcon sx={{ fontSize: 22 }} />
                                ) : (
                                    <ChevronRightIcon sx={{ fontSize: 22 }} />
                                )}
                            </IconButton>
                        ) : (
                            <Box sx={{ width: 16 }} />
                        )}
                        <Typography
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontWeight: node.isCritical ? 700 : 500,
                                fontSize: "14px",
                                color: "#3F3F3F",
                                lineHeight: "100%",
                            }}
                        >
                            {node.code}
                        </Typography>
                    </Box>

                    {/* Activity Name */}
                    <Typography
                        sx={{
                            fontFamily: "Outfit, sans-serif",
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#3F3F3F",
                            lineHeight: "130%",
                        }}
                    >
                        {node.name}
                    </Typography>

                    {/* Baseline End Date */}
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Chip
                            icon={<CiCalendar size={16} color="#433F5E" />}
                            label={formatDate(node.baselineEndDate)}
                            sx={{
                                backgroundColor: "#E7E7EB",
                                color: "#433F5E",
                                fontWeight: 600,
                                height: "22px",
                                fontSize: "11px",
                                borderRadius: "5px",
                                px: 1,
                            }}
                        />
                    </Box>

                    {/* % Complete */}
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Chip
                            label={`${node.percentageComplete}%`}
                            sx={{
                                backgroundColor: "#E2F6FF",
                                color: "#0A65E4",
                                fontWeight: 600,
                                height: "22px",
                                fontSize: "12px",
                                borderRadius: "5px",
                                px: 1,
                            }}
                        />
                    </Box>
                </Box>

                {hasChildren && isExpanded &&
                    node.children.map((child) => renderNode(child, level + 1))}
            </React.Fragment>
        );
    };

    // Optional: compute total "true critical" count from flatRows
    const totalCritical = flatRows.filter(
        (r) => (r.Critical ?? r.critical ?? false) === true
    ).length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "Outfit, sans-serif",
                            fontWeight: 500,
                            fontSize: "20px",
                            mb: "8px",
                            color: "#111216",
                            lineHeight: "120%",
                        }}
                    >
                        Critical Activity List
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "Outfit, sans-serif",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#7D7F81",
                            lineHeight: "140%",
                        }}
                    >
                        Track critical activities driving project progress and deadlines.
                    </Typography>
                </Box>

                <IconButton onClick={onClose}>
                    <RxCross2 />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ borderBottom: "1px solid #ECECEC" }}>
                    {/* Header row */}
                    <Box
                        sx={{
                            background: "#E7E7EB",
                            borderTop: "1px solid #ECECEC",
                            px: 2,
                            py: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: GRID_COLS,
                                gap: 1,
                                alignItems: "center",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <MdLineStyle size={16} color="#5B5772" />
                                <Typography
                                    sx={{
                                        fontFamily: "Outfit, sans-serif",
                                        fontWeight: 400,
                                        fontSize: "14px",
                                        color: "#5B5772",
                                        lineHeight: "100%",
                                    }}
                                >
                                    Activity Code
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <IoReorderThreeOutline size={16} color="#5B5772" />
                                <Typography
                                    sx={{
                                        fontFamily: "Outfit, sans-serif",
                                        fontWeight: 400,
                                        fontSize: "14px",
                                        color: "#5B5772",
                                        lineHeight: "100%",
                                    }}
                                >
                                    Activity Name
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    justifyContent: "center",
                                }}
                            >
                                <CiCalendar size={16} color="#5B5772" />
                                <Typography
                                    sx={{
                                        fontFamily: "Outfit, sans-serif",
                                        fontWeight: 400,
                                        fontSize: "14px",
                                        color: "#5B5772",
                                        lineHeight: "100%",
                                    }}
                                >
                                    Baseline/Forecast End Date
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    justifyContent: "center",
                                }}
                            >
                                <LuClockArrowDown size={16} color="#5B5772" />
                                <Typography
                                    sx={{
                                        fontFamily: "Outfit, sans-serif",
                                        fontWeight: 400,
                                        fontSize: "14px",
                                        color: "#5B5772",
                                        lineHeight: "100%",
                                    }}
                                >
                                    % Complete
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Body */}
                    {isLoading ? (
                        <Box sx={{ px: 2, py: 1 }}>
                            <LinearProgress />
                        </Box>
                    ) : isError ? (
                        <Box sx={{ px: 2, py: 2 }}>
                            <Typography color="error" sx={{ fontSize: "14px" }}>
                                Failed to load critical activities.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ maxHeight: 400, overflowY: "auto", px: 2, py: 1 }}>
                            {treeData.length === 0 ? (
                                <Typography sx={{ fontSize: "14px", color: "#6C757D" }}>
                                    No activities found.
                                </Typography>
                            ) : (
                                treeData.map((node) => renderNode(node, 0))
                            )}
                        </Box>
                    )}
                </Box>

                {/* Footer summary */}
                <Box>
                    <Paper
                        elevation={2}
                        sx={{
                            p: "15px 16px 20px 16px",
                            borderRadius: 2,
                            backgroundColor: "#E5E5E5",
                            border: "1px solid #e0e0e0",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    mb: 0,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "Outfit, sans-serif",
                                        fontWeight: 600,
                                        fontSize: "12px",
                                        color: "#6C757D",
                                        lineHeight: "100%",
                                    }}
                                >
                                    Total Critical Activities
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <Typography
                                        sx={{
                                            fontFamily: "Outfit, sans-serif",
                                            fontWeight: 700,
                                            fontSize: "16px",
                                            color: "#343A40",
                                            lineHeight: "100%",
                                        }}
                                    >
                                        {activityCount || 222}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default UpcomingCriticalActivityDialog;
